import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, ExternalLink, Mic, BookOpen, TrendingUp, Users } from 'lucide-react';

const Perspectives = () => {
  const aiFrameworks = [
    {
      title: 'AI Strategy Implementation Framework',
      description: 'Governance frameworks driving measurable ROI:',
      keyPoints: [
        'Strategic alignment with business objectives',
        'Cross-functional governance structure',
        'Measurable success metrics and KPIs',
        'Change management and adoption strategy'
      ],
      impact: 'Used by 3 Fortune 500 companies to accelerate AI adoption'
    },
    {
      title: 'Digital Transformation ROI Model',
      description: 'Structured roadmap for quantifying business impact:',
      keyPoints: [
        'Cost-benefit analysis methodology',
        'Risk assessment and mitigation strategies',
        'Timeline and milestone planning',
        'Stakeholder alignment and communication'
      ],
      impact: 'Helped organizations achieve 40% average ROI improvement'
    },
    {
      title: 'Business Development Ecosystem Design',
      description: 'Methodology for profitable strategic alliances:',
      keyPoints: [
        'Partner identification and evaluation',
        'Value proposition alignment',
        'Governance and relationship management',
        'Performance measurement and optimization'
      ],
      impact: 'Generated $50M+ in partnership value across multiple organizations'
    }
  ];

  const articles = [
    {
      title: 'AI Beyond Autonomy: Transforming the Automotive Value Chain',
      platform: 'LinkedIn',
      date: '2024',
      description: 'Exploring how AI is transforming the entire automotive value chain, from design to customer experience.',
      link: '#',
      readTime: '5 min read'
    },
    {
      title: 'Business Development for Digital Growth: Strategic Alliances in Transformation',
      platform: 'LinkedIn',
      date: '2024',
      description: 'How organizations can leverage partnerships to accelerate digital transformation and competitive advantage.',
      link: '#',
      readTime: '4 min read'
    },
    {
      title: 'Chief of Staff Role Evolution: Maximizing Executive Impact',
      platform: 'LinkedIn',
      date: '2023',
      description: 'The evolving role of Chief of Staff in modern organizations and how to maximize strategic impact.',
      link: '#',
      readTime: '6 min read'
    }
  ];

  const speaking = [
    {
      title: 'AI Strategy Summit 2024',
      event: 'Tech Leadership Conference',
      date: 'March 2024',
      description: 'Panelist on AI adoption',
      role: 'Panelist',
      audience: '200+ C-level executives'
    },
    {
      title: 'Digital Transformation Forum',
      event: 'Industry Innovation Summit',
      date: 'November 2023',
      description: 'Keynote on strategic execution',
      role: 'Keynote Speaker',
      audience: '500+ business leaders'
    },
    {
      title: 'Business Development Strategy Workshop',
      event: 'Business Development Conference',
      date: 'September 2023',
      description: 'Leader on ecosystem growth strategies',
      role: 'Workshop Leader',
      audience: '50+ partnership professionals'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Perspectives & Insights</h1>
            <p className="section-subtitle">
              Thought leadership on strategic transformation, AI innovation, and executive leadership
            </p>
            <p className="text-primary-700 text-lg leading-relaxed">
              Sharing frameworks, insights, and perspectives on how organizations can navigate 
              digital transformation, leverage AI strategically, and build sustainable competitive advantages.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Frameworks */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Strategic Frameworks</h2>
            <p className="section-subtitle">
              Proven methodologies for driving organizational transformation and strategic impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {aiFrameworks.map((framework, index) => (
              <motion.div
                key={framework.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card group hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-accent-100 rounded-xl flex items-center justify-center mb-6">
                  <Lightbulb size={32} className="text-primary-700" />
                </div>
                
                <h3 className="text-xl font-semibold text-primary-900 mb-3 group-hover:text-primary-700 transition-colors">
                  {framework.title}
                </h3>
                
                <p className="text-primary-700 mb-4">
                  {framework.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  {framework.keyPoints.map((point, pointIndex) => (
                    <div key={pointIndex} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-primary-800">{point}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-primary-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-primary-900">
                    {framework.impact}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex justify-center mt-8 mb-4"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>

      {/* Articles */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Latest Articles</h2>
            <p className="section-subtitle">
              Insights and perspectives on strategic leadership and digital transformation
            </p>
          </motion.div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl p-6 border border-primary-100 text-center"
            >
              <span className="text-primary-700 text-lg font-semibold">Coming soon.</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Speaking Engagements */}
      <section className="section bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Speaking Engagements</h2>
            <p className="section-subtitle">
              Sharing insights and expertise at industry conferences and leadership forums
            </p>
          </motion.div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-primary-50 to-white rounded-xl p-6 border border-primary-100 text-center"
            >
              <span className="text-primary-700 text-lg font-semibold">Coming soon.</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="section-title">Interested in These Perspectives?</h2>
            <p className="section-subtitle">
              Let's discuss how these frameworks and insights can be applied to your organization's strategic challenges
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary"
              >
                Start a Conversation
              </a>
              <a
                href="/projects"
                className="btn-secondary"
              >
                View Case Studies
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Perspectives; 